import { useState } from 'react'
import { useQuery } from '@apollo/client'
import { GET_SUGGESTION_SYSTEM_PROMPT } from '@/graphql/ads/prompt-suggestion-queries'
import { features } from '@/config/features'

export type SuggestionPromptKind = 'IMAGE_PROMPTS' | 'VIDEO_PROMPTS' | 'KEYWORDS' | 'PURPOSE' | 'BRIEF'

export interface SuggestionSystemPrompt {
    kind: SuggestionPromptKind
    prompt: string
    custom: boolean
}

export interface SuggestionSystemPromptData {
    suggestionSystemPrompt: SuggestionSystemPrompt
}

/**
 * What the system prompt editor changed: the prompt this dialog sends with each suggestion request instead of the
 * built-in one (only for a license that is not saved yet; null otherwise) and whether a custom prompt is now in effect.
 */
export interface SystemPromptChange {
    override: string | null
    custom: boolean
}

/** The variables naming the license, as a suggestion request sends them: a saved license or one not saved yet. */
export interface SuggestionTarget {
    campaignId?: string | null
    license?: object
}

/** The variables of a `suggestionSystemPrompt` query; `draft` is left out when the suggestions improve no text. */
export const systemPromptQueryVariablesOf = (
    target: SuggestionTarget,
    kind: SuggestionPromptKind,
    draft?: string | null,
) => ({ ...target, kind, ...(draft === undefined ? {} : { draft }) })

/** The `systemPrompt` variable of a suggestion request, sent only when the dialog uses its own prompt. */
export const systemPromptVariableOf = (override: string | null) => (override ? { systemPrompt: override } : {})

interface UseSuggestionSystemPromptOptions {
    /** The saved license; without one the edited prompt is only kept for the open dialog */
    campaignId?: string | null
    kind: SuggestionPromptKind
    /** True while the helper's dialog is open: a saved license's custom prompt is looked up each time it opens */
    active: boolean
}

/**
 * The system prompt state an AI helper keeps while its dialog is open: whether the editor is shown, the prompt the
 * dialog sends instead of the built-in one for a license that is not saved yet (`override`), and whether a custom
 * prompt writes the suggestions (`custom`), which for a saved license is read from the server when the dialog opens
 * and then follows what the editor saved. `apply` takes the editor's change, closes it and returns the override the
 * next request sends; `reset` drops everything when the dialog closes. While the `systemPromptEditing` feature is
 * off nothing is looked up, the editor never opens and no prompt counts as custom.
 */
export const useSuggestionSystemPrompt = ({ campaignId, kind, active }: UseSuggestionSystemPromptOptions) => {
    const [editing, setEditing] = useState(false)
    const [override, setOverride] = useState<string | null>(null)
    const [changedCustom, setChangedCustom] = useState<boolean | null>(null)
    const enabled = features.systemPromptEditing
    const { data } = useQuery<SuggestionSystemPromptData>(GET_SUGGESTION_SYSTEM_PROMPT, {
        variables: systemPromptQueryVariablesOf({ campaignId }, kind),
        skip: !enabled || !campaignId || !active,
        fetchPolicy: 'network-only',
    })

    const custom =
        enabled && (campaignId ? (changedCustom ?? data?.suggestionSystemPrompt?.custom ?? false) : override !== null)

    const apply = (change: SystemPromptChange) => {
        setEditing(false)
        setOverride(change.override)
        setChangedCustom(change.custom)
        return change.override
    }

    const reset = () => {
        setEditing(false)
        setOverride(null)
        setChangedCustom(null)
    }

    return {
        editing: enabled && editing,
        override,
        custom,
        edit: () => enabled && setEditing(true),
        cancel: () => setEditing(false),
        apply,
        reset,
    }
}
